import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxOpen, FaCalendarAlt, FaChartLine, FaCog, FaEdit, FaEnvelope, FaEye, FaEyeSlash, FaFileDownload, FaFileUpload, FaList, FaMapMarkerAlt, FaMoneyBill, FaMoneyBillWave, FaPlus, FaPrint, FaQrcode, FaReceipt, FaSearch, FaStore, FaTag, FaTicketAlt, FaTrash, FaUndoAlt, FaUser, FaWhatsapp } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import Swal from 'sweetalert2';
import { authActions } from '../../sagas/authSlice';

const ProfilePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { businessName, address, telephone, fullName, email } = useSelector(state => state.auth);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [error, setError] = useState('');

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setError(t('profile.passwordMismatch'));
            return;
        }
        try {
            dispatch(authActions.changePassword({
                form: passwordForm, onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('profile.passwordChanged'),
                        showConfirmButton: false,
                        timer: 1500
                    });
                    setPasswordForm({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                    });
                },
                onError: (error) => {
                    Swal.fire({
                        icon: 'error',
                        title: t('profile.changePasswordError'),
                    });
                }
            }));
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: error.message
            });
        }
    };

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0 d-flex align-items-center">
                                    <FaCog className="text-primary me-2" />
                                    {t('profile.pageTitle')}
                                </h4>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Card className="shadow-sm">
                        <CardBody>
                            <h5 className="mb-4">{t('profile.businessInfo')}</h5>
                            <FormGroup>
                                <Label>{t('profile.businessName')}</Label>
                                <Input value={businessName} disabled />
                            </FormGroup>
                            <FormGroup>
                                <Label>{t('profile.address')}</Label>
                                <Input value={address} disabled />
                            </FormGroup>
                            <FormGroup>
                                <Label>{t('profile.telephone')}</Label>
                                <Input value={telephone} disabled />
                            </FormGroup>
                            <FormGroup>
                                <Label>{t('profile.email')}</Label>
                                <Input value={email} disabled />
                            </FormGroup>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="shadow-sm">
                        <CardBody>
                            <h5 className="mb-4">{t('profile.changePassword')}</h5>
                            <Form onSubmit={handlePasswordChange}>
                                <FormGroup>
                                    <Label>{t('profile.currentPassword')}</Label>
                                    <div className="position-relative">
                                        <Input
                                            type={showPasswords.current ? "text" : "password"}
                                            value={passwordForm.currentPassword}
                                            onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                            required
                                        />
                                        <Button
                                            type="button"
                                            color="link"
                                            className="position-absolute end-0 top-50 translate-middle-y border-0"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPasswords({ ...showPasswords, current: !showPasswords.current })
                                                e.currentTarget.blur();
                                            }}
                                            tabIndex={-1}
                                        >
                                            {showPasswords.current ? <FaEye /> : <FaEyeSlash />}
                                        </Button>
                                    </div>
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('profile.newPassword')}</Label>
                                    <div className="position-relative">
                                        <Input
                                            type={showPasswords.new ? "text" : "password"}
                                            value={passwordForm.newPassword}
                                            onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                            required
                                        />
                                        <Button
                                            type="button"
                                            color="link"
                                            className="position-absolute end-0 top-50 translate-middle-y border-0"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPasswords({ ...showPasswords, new: !showPasswords.new })
                                                e.currentTarget.blur();
                                            }}
                                            tabIndex={-1}
                                        >
                                            {showPasswords.new ? <FaEye /> : <FaEyeSlash />}
                                        </Button>
                                    </div>
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('profile.confirmPassword')}</Label>
                                    <div className="position-relative">
                                        <Input
                                            type={showPasswords.confirm ? "text" : "password"}
                                            value={passwordForm.confirmPassword}
                                            onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                            required
                                        />
                                        <Button
                                            type="button"
                                            color="link"
                                            className="position-absolute end-0 top-50 translate-middle-y border-0"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })
                                                e.currentTarget.blur();
                                            }}
                                            tabIndex={-1}
                                        >
                                            {showPasswords.confirm ? <FaEye /> : <FaEyeSlash />}
                                        </Button>
                                    </div>
                                </FormGroup>

                                {error && <div className="text-danger mb-3">{error}</div>}
                                <Button color="primary" type="submit">
                                    {t('common.save')}
                                </Button>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ProfilePage;